import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Loader2, Users } from "lucide-react";
import { InviteTenantAdminDialog } from "./InviteTenantAdminDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABEL = { tenant_admin: "Tenant admin", admin: "Location admin", manager: "Manager", agent: "Staff", super_admin: "Super admin" };

function UserRow({ u, tenantId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const isTA = u.role === "tenant_admin";
  const canToggle = (isTA || u.role === "admin") && !u.invite_pending;
  const resend = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/admin/tenants/${tenantId}/users/${u.id}/resend-invite`);
      toast.success(res.data.message);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend invite");
    } finally {
      setResending(false);
    }
  };
  const toggle = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/users/${u.id}/tenant-admin`, { is_tenant_admin: !isTA });
      toast.success(isTA ? `${u.email} is now a location admin` : `${u.email} now sees every location in this tenant`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    } finally {
      setBusy(false);
    }
  };
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0" data-testid={`tenant-user-row-${u.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-gray-900 truncate">{name || u.email}</div>
        <div className="text-xs text-gray-500">{name ? `${u.email} · ` : ""}Home: {u.home_location_name || "—"}</div>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded border ${isTA ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}
          data-testid={`tenant-user-role-${u.id}`}
        >
          {isTA && <Crown className="inline h-3 w-3 mr-1 -mt-0.5" />}{ROLE_LABEL[u.role] || u.role}
        </span>
        {u.invite_pending && (
          <>
            <span className="text-xs font-semibold px-2 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200" data-testid={`invite-pending-${u.id}`}>
              Invite pending{u.invited_at ? ` · sent ${new Date(u.invited_at).toLocaleDateString()}` : ""}
            </span>
            <Button size="sm" variant="outline" onClick={resend} disabled={resending} data-testid={`resend-invite-${u.id}`}>
              {resending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Resend
            </Button>
          </>
        )}
        {canToggle && (
          <Button size="sm" variant="outline" onClick={toggle} disabled={busy} data-testid={`toggle-tenant-admin-${u.id}`}>
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {isTA ? "Remove tenant admin" : "Make tenant admin"}
          </Button>
        )}
      </div>
    </div>
  );
}

export const TenantUsersPanel = ({ detail, onChanged }) => {
  const users = detail.users || [];
  return (
    <div className="mt-5" data-testid={`tenant-users-panel-${detail.id}`}>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-800 flex-wrap">
        <Users className="h-4 w-4 text-indigo-600" /> Users ({users.length})
        <span className="text-xs font-normal text-gray-500">· Tenant admins automatically see every location in this tenant</span>
        <span className="ml-auto"><InviteTenantAdminDialog detail={detail} onDone={onChanged} /></span>
      </div>
      {users.length === 0 ? (
        <div className="text-sm text-gray-500" data-testid="tenant-users-empty">No users in this tenant yet.</div>
      ) : (
        <div className="bg-white rounded-md border px-3">
          {users.map((u) => <UserRow key={u.id} u={u} tenantId={detail.id} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
};
